import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal,
  Radio, Row, Select, Space, Tabs, Tag, Typography, message
} from 'antd';
import {
  SaveOutlined, CheckOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { paymentVoucherApi } from './paymentVoucherApi';
import {
  PAYMENT_MODES, PAYMENT_TYPE_LABEL, PAYMENT_TYPE_COLOR,
  PAYMENT_STATUS_COLOR, PAYMENT_STATUS_LABEL,
  CHEQUE_STATUSES,
  formatINR
} from './paymentVoucherConstants';
// Stage 6B.7D — audit log + accounting attachments view tabs.
import AuditLogPanel from '../AuditLogPanel';
import AttachmentsPanel from '../AttachmentsPanel';
import '../accounts.css';

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Payment / Receipt Voucher Add/Edit/View modal (Stage 6B.6).
//
// One 1080-wide centered modal handles both payment_type='payment'
// (supplier) and payment_type='receipt' (customer). The type radio
// at the top of the form drives which party dropdown is shown.
//
// A live "posting preview" panel at the bottom shows the 2-line
// DR/CR explosion the backend will generate so the operator can
// sanity-check before saving + posting. Post + Cancel are handled
// by the parent page so the Approval-MFA modal doesn't stack on
// top of this one — same pattern as the Journal Voucher modal.
// ─────────────────────────────────────────────────────────────────

const READONLY_STATUSES = new Set(['posted', 'cancelled']);

function emptyDefaults() {
  return {
    paymentType:     'payment',
    paymentDate:     dayjs(),
    partyId:         null,
    amount:          null,
    mode:            'bank',
    bankReference:   '',
    // Stage 6B.7C — cheque tracking. Shown only when mode='cheque'.
    chequeNo:        '',
    chequeDate:      null,
    chequeStatus:    'pending',
    chequeBankName:  '',
    ledgerAccountId: null,
    remarks:         ''
  };
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) return emptyDefaults();
  return {
    paymentType:     record.paymentType || 'payment',
    paymentDate:     record.paymentDate ? dayjs(record.paymentDate) : dayjs(),
    partyId:         record.partyId || null,
    amount:          Number(record.amount) || null,
    mode:            record.mode || 'bank',
    bankReference:   record.bankReference || '',
    chequeNo:        record.chequeNo || '',
    chequeDate:      record.chequeDate ? dayjs(record.chequeDate) : null,
    chequeStatus:    record.chequeStatus || (record.mode === 'cheque' ? 'pending' : null),
    chequeBankName:  record.chequeBankName || '',
    ledgerAccountId: record.ledgerAccountId || null,
    remarks:         record.remarks || ''
  };
}

// Searchable party Select. Either supplierLookup or customerLookup
// is wired depending on payment_type — the parent passes `kind`.
function PartySelect({ kind, value, onChange, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetcher = kind === 'supplier'
    ? paymentVoucherApi.supplierLookup
    : paymentVoucherApi.customerLookup;

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await fetcher(q || '', 100);
      setOptions(rows);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [fetcher]);

  useEffect(() => { load(''); }, [load, kind]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(text), 200);
  }, [load]);

  return (
    <Select
      style={{ width: '100%' }}
      placeholder={kind === 'supplier' ? 'Select supplier…' : 'Select customer…'}
      showSearch
      disabled={disabled}
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : (kind === 'supplier' ? 'No suppliers' : 'No customers')}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>
              {kind === 'supplier' ? o.supplierCode : o.customerCode}
            </code>
            {kind === 'supplier' ? o.supplierName : o.customerName}
            {o.gstin ? (
              <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· {o.gstin}</span>
            ) : null}
          </span>
        )
      }))}
    />
  );
}

// Searchable cash/bank ledger Select.
function CashBankLedgerSelect({ value, onChange, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await paymentVoucherApi.cashBankLedgerLookup(q || '', 100);
      setOptions(rows);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(text), 200);
  }, [load]);

  return (
    <Select
      style={{ width: '100%' }}
      placeholder="Select cash/bank ledger…"
      showSearch
      disabled={disabled}
      value={value || undefined}
      onChange={(v) => onChange(v)}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      notFoundContent={loading ? 'Loading…' : 'No cash/bank ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
            {o.accountName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>· {o.accountGroup}</span>
          </span>
        )
      }))}
    />
  );
}

export default function PaymentVoucherModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [paymentType, setPaymentType] = useState('payment');
  const [partyId, setPartyId] = useState(null);
  const [ledgerAccountId, setLedgerAccountId] = useState(null);
  const [amount, setAmount] = useState(null);

  // Stage 6B.7C — track mode reactively so the cheque sub-form
  // shows / hides without remounting the parent. `Form.useWatch`
  // re-renders this component when the form's `mode` field changes.
  const watchedMode = Form.useWatch('mode', form);
  const isCheque    = watchedMode === 'cheque';

  const isPosted   = record && READONLY_STATUSES.has(record.status);
  const readOnly   = mode === 'view' || isPosted;
  const status     = record ? record.status : 'draft';

  // Caches for displaying the resolved party + ledger names in the
  // posting preview without an extra fetch per render.
  const partyLabelCacheRef = useRef(new Map());
  const ledgerLabelCacheRef = useRef(new Map());

  // Reset form when opening
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const d = defaultsFor(mode, record);
    setPaymentType(d.paymentType);
    setPartyId(d.partyId);
    setLedgerAccountId(d.ledgerAccountId);
    setAmount(d.amount);
    form.setFieldsValue(d);
  }, [open, mode, record, form]);

  // Seed party / ledger caches with the record's joined names so the
  // preview renders immediately without waiting for the dropdown load.
  useEffect(() => {
    if (!record) return;
    if (record.partyId && record.partyName) {
      partyLabelCacheRef.current.set(record.partyId, {
        name: record.partyName, code: record.partyCode
      });
    }
    if (record.ledgerAccountId && record.ledgerName) {
      ledgerLabelCacheRef.current.set(record.ledgerAccountId, {
        name: record.ledgerName, code: record.ledgerCode
      });
    }
  }, [record]);

  // Background-load the party + ledger names whenever the operator
  // picks a fresh value (so the preview can show "Pay X to <name>"
  // instead of "Pay X to <uuid>").
  useEffect(() => {
    if (!partyId) return;
    if (partyLabelCacheRef.current.has(partyId)) return;
    (async () => {
      try {
        const rows = paymentType === 'payment'
          ? await paymentVoucherApi.supplierLookup('', 200)
          : await paymentVoucherApi.customerLookup('', 200);
        for (const r of rows) {
          partyLabelCacheRef.current.set(r.id, {
            name: paymentType === 'payment' ? r.supplierName : r.customerName,
            code: paymentType === 'payment' ? r.supplierCode : r.customerCode
          });
        }
      } catch (_e) { /* silent */ }
    })();
  }, [partyId, paymentType]);

  useEffect(() => {
    if (!ledgerAccountId) return;
    if (ledgerLabelCacheRef.current.has(ledgerAccountId)) return;
    (async () => {
      try {
        const rows = await paymentVoucherApi.cashBankLedgerLookup('', 200);
        for (const r of rows) {
          ledgerLabelCacheRef.current.set(r.id, {
            name: r.accountName, code: r.accountCode
          });
        }
      } catch (_e) { /* silent */ }
    })();
  }, [ledgerAccountId]);

  const onChangePaymentType = useCallback((v) => {
    setPaymentType(v);
    // Clear party — supplier list and customer list are disjoint.
    setPartyId(null);
    form.setFieldsValue({ paymentType: v, partyId: null });
  }, [form]);

  const buildPreview = useCallback(() => {
    const amt = Number(amount) || 0;
    const partyMeta   = partyId           ? partyLabelCacheRef.current.get(partyId)             : null;
    const ledgerMeta  = ledgerAccountId   ? ledgerLabelCacheRef.current.get(ledgerAccountId)    : null;
    const partyLine   = partyMeta  ? `${partyMeta.code || ''} ${partyMeta.name || ''}`.trim()  : 'Party ledger';
    const ledgerLine  = ledgerMeta ? `${ledgerMeta.code || ''} ${ledgerMeta.name || ''}`.trim() : 'Cash / Bank ledger';
    return paymentType === 'payment'
      ? [
          { side: 'Dr', label: partyLine,  amount: amt },
          { side: 'Cr', label: ledgerLine, amount: amt }
        ]
      : [
          { side: 'Dr', label: ledgerLine, amount: amt },
          { side: 'Cr', label: partyLine,  amount: amt }
        ];
  }, [amount, partyId, ledgerAccountId, paymentType]);

  const preview = buildPreview();
  const totalDr = preview.filter((l) => l.side === 'Dr').reduce((a, l) => a + l.amount, 0);
  const totalCr = preview.filter((l) => l.side === 'Cr').reduce((a, l) => a + l.amount, 0);
  const balanced = totalDr === totalCr && totalDr > 0;

  const saveDraft = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setBusy(true);
      const isChequeMode = values.mode === 'cheque';
      const payload = {
        paymentType:     values.paymentType,
        paymentDate:     values.paymentDate.format('YYYY-MM-DD'),
        partyType:       values.paymentType === 'payment' ? 'supplier' : 'customer',
        partyId:         values.partyId,
        amount:          Number(values.amount) || 0,
        mode:            values.mode,
        bankReference:   isChequeMode ? null : (values.bankReference || null),
        // Stage 6B.7C — cheque fields only sent when mode='cheque'.
        // On any other mode we explicitly null them so a leftover value
        // in the form state can never silently persist.
        chequeNo:        isChequeMode ? (values.chequeNo       || null) : null,
        chequeDate:      isChequeMode && values.chequeDate
                           ? values.chequeDate.format('YYYY-MM-DD')
                           : null,
        chequeStatus:    isChequeMode ? (values.chequeStatus   || null) : null,
        chequeBankName:  isChequeMode ? (values.chequeBankName || null) : null,
        ledgerAccountId: values.ledgerAccountId,
        remarks:         values.remarks || null
      };
      let saved;
      if (mode === 'add') {
        saved = await paymentVoucherApi.createDraft(payload);
        message.success(`Draft ${saved.paymentNo} saved.`);
      } else if (mode === 'edit') {
        // For UPDATE we don't send paymentType (it's immutable once draft is
        // created, since paymentNo is from the type-specific sequence).
        const { paymentType: _pt, ...patch } = payload;
        saved = await paymentVoucherApi.updateDraft(record.id, patch);
        message.success('Draft updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) {
        // Form.validateFields returns this shape; the form already
        // surfaces the per-field error. Nothing extra to do.
        return;
      }
      message.error(e?.response?.data?.error?.message || 'Save failed.');
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  const title = useMemo(() => {
    const t = paymentType === 'payment' ? 'Payment Voucher' : 'Receipt Voucher';
    const vn = record && record.paymentNo ? ' — ' + record.paymentNo : '';
    if (mode === 'add')  return `New ${t}`;
    if (mode === 'view') return `View ${t}${vn}`;
    return `Edit ${t}${vn}`;
  }, [mode, paymentType, record]);

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={saveDraft}>
          {mode === 'add' ? 'Save Draft' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  // Stage UI-FIX (Receipt/Payment focus loss): the previous structure
  // declared `function DetailsBody()` inside this component's body, so
  // every parent re-render (which fires on EVERY keystroke through
  // setAmount / setLedgerAccountId / Form.useWatch) produced a brand
  // new function identity. React treated `<DetailsBody />` as a fresh
  // component type each render and unmounted + remounted the entire
  // form → focus on the Amount input was lost after one character, and
  // the form's pending-validation state ("Cash/Bank ledger is required")
  // flashed before the operator could pick a value.
  //
  // Replacing the inner-component with a JSX value (`detailsBody`) that
  // is evaluated as part of the same render keeps the underlying
  // <Form>, <Input>, <Select> element TYPES stable across renders, so
  // React's reconciler patches them in-place instead of remounting.
  // Behaviour, validation rules, closures, posting preview and save
  // flow are identical to the previous build.
  const detailsBody = (<>

      <Form className="erp-accounts-form" form={form} layout="vertical" preserve initialValues={emptyDefaults()}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Voucher No">
              <Input value={record?.paymentNo || (mode === 'add' ? '(auto-generated)' : '')} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Date" name="paymentDate"
              rules={[{ required: true, message: 'Date is required' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Type" name="paymentType"
              rules={[{ required: true, message: 'Type is required' }]}>
              <Radio.Group
                disabled={readOnly || mode === 'edit'}
                onChange={(e) => onChangePaymentType(e.target.value)}>
                <Radio.Button value="payment">Payment (out)</Radio.Button>
                <Radio.Button value="receipt">Receipt (in)</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={paymentType === 'payment' ? 'Supplier' : 'Customer'}
              name="partyId"
              rules={[{ required: true, message: 'Party is required' }]}
            >
              <PartySelect
                kind={paymentType === 'payment' ? 'supplier' : 'customer'}
                value={partyId}
                onChange={(v) => { setPartyId(v); form.setFieldsValue({ partyId: v }); }}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Mode" name="mode"
              rules={[{ required: true, message: 'Mode is required' }]}>
              <Select disabled={readOnly}
                options={PAYMENT_MODES.map((m) => ({ value: m.value, label: m.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Amount (₹)" name="amount"
              rules={[
                { required: true, message: 'Amount is required' },
                { type: 'number', min: 0.01, message: 'Amount must be > 0' }
              ]}>
              <InputNumber
                style={{ width: '100%' }} min={0.01} step={1} precision={2}
                disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0}
                onChange={(n) => setAmount(n)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Cash / Bank Ledger" name="ledgerAccountId"
              rules={[{ required: true, message: 'Cash/Bank ledger is required' }]}>
              <CashBankLedgerSelect
                value={ledgerAccountId}
                onChange={(v) => { setLedgerAccountId(v); form.setFieldsValue({ ledgerAccountId: v }); }}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Bank / UPI Reference" name="bankReference"
              tooltip="UTR / UPI ref / NEFT reference. For mode='cheque', use the dedicated cheque fields below instead.">
              <Input maxLength={100} disabled={readOnly || isCheque}
                placeholder="UTR / UPI ref id (optional)" />
            </Form.Item>
          </Col>
        </Row>

        {/* Stage 6B.7C — cheque-mode sub-form. Hidden on every other
            mode; rendered (but not auto-required) when mode='cheque'.
            The chequeNo + chequeStatus are required at POST time and
            also enforced by the DB CHECK constraint. */}
        {isCheque ? (
          <Row gutter={12}>
            <Col xs={24} md={6}>
              <Form.Item label="Cheque No" name="chequeNo"
                rules={[{ max: 40, message: 'Up to 40 characters' }]}>
                <Input maxLength={40} disabled={readOnly}
                  placeholder="e.g. 145872" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Cheque Date" name="chequeDate">
                <DatePicker style={{ width: '100%' }} disabled={readOnly}
                  format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Cheque Status" name="chequeStatus">
                <Select disabled={readOnly} allowClear
                  placeholder="Pending / Deposited / Cleared / Bounced"
                  options={CHEQUE_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Bank Name (on cheque)" name="chequeBankName">
                <Input maxLength={120} disabled={readOnly}
                  placeholder="e.g. HDFC Bank" />
              </Form.Item>
            </Col>
          </Row>
        ) : null}

        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} disabled={readOnly}
            placeholder="Narration for this payment / receipt" />
        </Form.Item>
      </Form>

      {/* Posting preview */}
      <div className="erp-accounts-preview">
        <div className="erp-accounts-preview__title">
          Posting preview
          <Tag color={PAYMENT_TYPE_COLOR[paymentType]} style={{ marginInlineStart: 8 }}>
            {PAYMENT_TYPE_LABEL[paymentType]}
          </Tag>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: 56 }}>Side</th>
              <th>Ledger</th>
              <th style={{ textAlign: 'right', width: 140 }}>Debit (₹)</th>
              <th style={{ textAlign: 'right', width: 140 }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {preview.map((l, i) => (
              <tr key={i}>
                <td>
                  <Tag color={l.side === 'Dr' ? 'blue' : 'magenta'}
                       style={{ marginInlineEnd: 0 }}>
                    {l.side}
                  </Tag>
                </td>
                <td>{l.label}</td>
                <td style={{ textAlign: 'right' }}>
                  {l.side === 'Dr' ? formatINR(l.amount) : <Text type="secondary">—</Text>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {l.side === 'Cr' ? formatINR(l.amount) : <Text type="secondary">—</Text>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }}>Total</td>
              <td style={{ textAlign: 'right' }}>₹ {formatINR(totalDr)}</td>
              <td style={{ textAlign: 'right' }}>₹ {formatINR(totalCr)}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ textAlign: 'right' }} className="erp-accounts-preview__sub">Difference</td>
              <td colSpan={2} style={{ textAlign: 'right',
                  color: balanced ? '#3f8600' : '#cf1322', fontWeight: 600 }}>
                ₹ {formatINR(Math.abs(totalDr - totalCr))} {balanced
                  ? <CheckOutlined /> : <CloseCircleOutlined />}
              </td>
            </tr>
          </tfoot>
        </table>
        {!balanced ? (
          <Alert
            type="info" showIcon style={{ marginTop: 10 }}
            message="Draft can be saved even if incomplete. Posting requires the amount and both ledgers to be set." />
        ) : null}
      </div>

      {status === 'cancelled' && record && record.cancelReason ? (
        <Alert type="error" showIcon style={{ marginTop: 12 }}
          message="Cancelled voucher"
          description={<><b>Reason:</b> {record.cancelReason}</>} />
      ) : null}
    </>);

  return (
    <Modal
      title={
        <span>
          {title}
          {record && record.status ? (
            <Tag color={PAYMENT_STATUS_COLOR[record.status] || 'default'}
                 style={{ marginInlineStart: 12 }}>
              {(PAYMENT_STATUS_LABEL[record.status] || record.status).toUpperCase()}
            </Tag>
          ) : null}
          {record && record.voucherNo ? (
            <Tag color="blue" style={{ marginInlineStart: 8 }}>
              JV {record.voucherNo}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      {/* Stage 6B.7D — Audit + Attachments tabs only in view mode.
          paymentType drives the audit `entityType` mapping below. */}
      {mode === 'view' && record && record.id ? (
        <Tabs size="small" defaultActiveKey="details" items={[
          { key: 'details', label: 'Details', children: detailsBody },
          { key: 'audit',   label: 'Audit',   children: (
            <AuditLogPanel
              entityType={record.paymentType === 'receipt' ? 'receipt' : 'payment'}
              entityId={record.id} />
          ) },
          { key: 'attachments', label: 'Attachments', children: (
            <AttachmentsPanel
              entityType={record.paymentType === 'receipt' ? 'receipt' : 'payment'}
              entityId={record.id}
              canUpload canDelete />
          ) }
        ]} />
      ) : (
        detailsBody
      )}
    </Modal>
  );
}
